import React, { useCallback, useEffect, useState } from 'react';

import moment, { Moment } from 'moment';

import { IconButton, Slider } from '@material-ui/core';

import CloseIcon from '@material-ui/icons/Close';
import MovieIcon from '@material-ui/icons/Movie';

import {
    activateDialog,
    activateGridWithSelectedOptions,
    IActivateGridParams,
    TDialog,
} from '../../../../../../../../state/ui/discovery/snapshotting';

import {
    useAppDispatch,
    useToolkitDispatch,
} from '../../../../../../../../hooks';

import TimeFormatter from '../../../../../../../../helpers/TimeFormatter';
import TranslationHelper from '../../../../../../../../helpers/TranslationHelper';

import { makeSourceSetUrl } from '../../../../../../../../services/discovery/_shared/urlMakers';

import {
    CAMERAS_RANGE,
    MAX_RANGE,
} from '../../../../../../../../constants/videoPlayerSettings';

import PlayerActionsContainer from './components/PlayerActionsContainer';

import AppConfig from '../../../../../../../../constants/AppConfig';
import { useStyles } from './Themable.hooks';
import {
    useCreatorLevel,
    useDiscoveryUIDialog,
    usePreviewElementId,
} from '../../../../../../selectors/index.hooks';
import {
    useFrameElementDate,
    useFrameElementMonitoredId,
    useFramesMoviesIds,
    useSourceSetElementDate,
    useSourceSetElementMonitoredId,
    useSourceSetElementStartDate,
    useSourceSetMovieFramesIds,
} from '../../../../../../selectors/composed/index.hooks';
import {
    IPlayerSettings,
    setPlayerSettings,
} from '../../../../../../../../state/ui/discoverySlice';
import { usePlayerSettings } from '../../../../../../../../state/ui/discoverySlice/index.hooks';
import useLocalStorage from '../../../../../../../../hooks/useLocalStorage';
import { usePrivileges } from '../../../../../../../../state/auth/index.hooks';
import { isPlayerEnabled } from '../../../../../../../../helpers/isPlayerEnabled';
import { getMovieDownloadUrl } from '../../../../../_utils/getMovieDownloadUrl';
import { ALL_CAMERAS_ID } from '../../../../../../../../components/dialogs/VideoPlayerDialog/VideoPlayerDialog';
import { isEmpty } from 'lodash';

interface IOwnProps {
    paramName: string;
    type?: string;
    reportId: string;
    initPlayerSettings: IPlayerSettings;
}

const handleReopenVideoDialog = (
    dialog: TDialog,
    showPlayer: boolean,
    setShowVideoPlayerActions: (bool: boolean) => void,
    setDialogSlider: (arr: number[]) => void,
    handleChangeValues: (settings: IPlayerSettings) => void,
    paramName: string
) => {
    const { sliderTimeRange } = dialog?.videoParams || {};
    if (showPlayer) {
        if (dialog?.elementCollectionName === paramName) {
            setShowVideoPlayerActions(true);
            sliderTimeRange && setDialogSlider(sliderTimeRange);
            // @ts-ignore
            handleChangeValues({ ...dialog?.videoParams?.playerSettings });
        }
    }
};

const formatDateToSliderValues = (
    getEventDate: () => Moment,
    startDate: unknown,
    setValue: (arr: number[]) => void
) => {
    const momentEventDate = getEventDate();
    const momentStartDate = moment(startDate as string);
    if (momentStartDate.isValid() && momentEventDate.isAfter(momentStartDate)) {
        const refDate = momentEventDate.seconds(0);
        const start = momentStartDate.subtract(20, 'seconds').seconds(0);
        const end = momentEventDate.add(20, 'seconds').seconds(0);

        const startDiff = Math.max(start.diff(refDate, 'minutes'), -15);
        const endDiff = Math.min(end.diff(refDate, 'minutes'), 15);

        setValue([startDiff, endDiff]);
    }
};

const PhotoOverlay = ({
    paramName,
    type,
    initPlayerSettings,
    reportId,
}: IOwnProps) => {
    const moviePlayerConfig = AppConfig.instance.getConfigKey(
        AppConfig.PROPERTY_MOVIE_PLAYER
    );

    const playerSettingsStorageName = 'playerSettings';

    const elementId = usePreviewElementId();
    const startDate = useSourceSetElementStartDate();
    const showPlayer = isPlayerEnabled();
    const sourceSetElementMonitoredId = useSourceSetElementMonitoredId();
    const frameElementMonitoredId = useFrameElementMonitoredId();
    const sourceSetFramesIds = useSourceSetMovieFramesIds(reportId);
    const framesIds = useFramesMoviesIds();
    const cameraIds = sourceSetFramesIds || framesIds || [];
    const ids =
        cameraIds.length > 1 ? [...cameraIds, ALL_CAMERAS_ID] : cameraIds;
    const date = useFrameElementDate();
    const eventDate = useSourceSetElementDate();
    const dialog = useDiscoveryUIDialog();
    const creatorLevel = useCreatorLevel();
    const classes = useStyles();
    const dispatch = useAppDispatch();
    const toolkitDispatch = useToolkitDispatch();
    const privileges = usePrivileges();

    const [showVideoPlayerActions, setShowVideoPlayerActions] = useState(false);
    const [sliderTimeRange, setSliderTimeRange] = useState<number[]>(
        moviePlayerConfig.initialRange || CAMERAS_RANGE
    );
    const [, setLocalStorageSettings] = useLocalStorage(
        playerSettingsStorageName,
        null
    );
    const playerSettings = usePlayerSettings();

    const monitoredId = sourceSetElementMonitoredId || frameElementMonitoredId;

    const handleChangePlayerSettings = (settings: IPlayerSettings) => {
        toolkitDispatch(setPlayerSettings(settings));
    };

    const handleChangeAnonymize = (settings: IPlayerSettings) => {
        handleChangePlayerSettings(settings);
    };

    useEffect(() => {
        if (privileges && !privileges.videoProcessingDebug) {
            if (!privileges.anonymizeSwitchVisible) {
                toolkitDispatch(
                    setPlayerSettings({ anonymize: privileges.anonymize })
                );
            } else {
                toolkitDispatch(
                    setPlayerSettings({
                        anonymize: initPlayerSettings?.anonymize,
                    })
                );
            }
        } else if (initPlayerSettings) {
            toolkitDispatch(setPlayerSettings(initPlayerSettings));
        }
        formatDateToSliderValues(getEventDate, startDate, setSliderTimeRange);
    }, []);

    useEffect(() => {
        if (!isEmpty(playerSettings)) {
            setLocalStorageSettings(playerSettings);
        }
    }, [playerSettings]);

    useEffect(() => {
        handleReopenVideoDialog(
            dialog,
            showPlayer,
            setShowVideoPlayerActions,
            setSliderTimeRange,
            handleChangeAnonymize,
            paramName
        );
    }, [dialog]);

    const isInFrameContext = type === 'frameDetails';

    const getEventDate = () => {
        return isInFrameContext
            ? moment(date as string).local()
            : moment(eventDate as string);
    };

    const marks = [
        {
            value: 0,
            label: `${TranslationHelper.translate(
                'Event'
            )} ${getEventDate().format('HH:mm:ss')}`,
        },
    ];

    const getLabelFormatter = (labelValue: number) => {
        const labelDate = getEventDate().add(labelValue, 'minutes');
        if (sliderTimeRange[1] === labelValue) {
            labelDate.add(1, 'minute');
        }
        return labelDate.format('HH:mm');
    };

    const setSliderValue = (e: any, newValue: number | number[]) => {
        if (Array.isArray(newValue)) {
            const from = Math.min(newValue[0], 0);
            const to = Math.max(newValue[1], 0);
            setSliderTimeRange([from, to]);
        }
    };

    const handleActivatePlayerDialog = useCallback(() => {
        dispatch(
            activateDialog(
                paramName || '',
                elementId,
                'sourceSetElementMoviePlayer',
                'preview',
                {
                    type: 'source-set-element-movieplayer',
                    level: creatorLevel + 1,
                    videoParams: {
                        sliderTimeRange,
                        playerSettings,
                        reportId,
                        ids,
                    },
                    context: type,
                }
            )
        );
    }, [playerSettings, sliderTimeRange]);

    const handleGetMovieDownloadUrl = (paramName: string) => {
        return getMovieDownloadUrl({
            paramName,
            hls: undefined,
            sliderTimeRange: sliderTimeRange || [-1, 1],
            playerSettings,
            isInFrameContext,
            date,
            monitoredId,
            elementId,
        });
    };
    const handleOpenFrameDetailsInGrid = (anonymizeImages: boolean) => {
        if (monitoredId) {
            const refDate = isInFrameContext ? date : eventDate;
            const action = {
                api: `/rest/api/source-sets/frames`,
                method: 'GET',
                label: '',
                params: {
                    from: TimeFormatter.toLocalDate(
                        moment(refDate as string)
                            .add(sliderTimeRange[0], 'minutes')
                            .seconds(0)
                    ),
                    to: TimeFormatter.toLocalDate(
                        moment(refDate as string).add(
                            sliderTimeRange[1],
                            'minutes'
                        )
                    ),
                    paramName: 'GPX',
                    monitoredId,
                },
            };
            const params: IActivateGridParams = {
                sourceSetId: makeSourceSetUrl(action).toString(),
                snapshot: {
                    type: 'source-set-grid',
                    level: creatorLevel + 1,
                },
                action,
                anonymizeImages,
                actionsToTrigger: [{ type: 'showFrame', date: refDate || '' }],
            };
            dispatch(activateGridWithSelectedOptions(params));
        }
    };

    return !showVideoPlayerActions ? (
        <IconButton
            className={classes.photoOverlayButton}
            onClick={() => setShowVideoPlayerActions(true)}
            role="moviesettings"
        >
            <MovieIcon />
        </IconButton>
    ) : (
        <div className={classes.settingsContainer}>
            <IconButton
                className={classes.settingsButton}
                onClick={() => setShowVideoPlayerActions(false)}
            >
                <CloseIcon />
            </IconButton>
            <div className={classes.settings}>
                {TranslationHelper.translate('Select video time range')}
                <Slider
                    value={sliderTimeRange}
                    min={moviePlayerConfig?.maxRange?.minus || MAX_RANGE.minus}
                    max={moviePlayerConfig?.maxRange?.plus || MAX_RANGE.plus}
                    onChange={setSliderValue}
                    valueLabelDisplay="on"
                    marks={marks}
                    valueLabelFormat={getLabelFormatter}
                    classes={{
                        root: classes.slider,
                        mark: classes.sliderMark,
                        markLabel: classes.sliderMarkLabel,
                        rail: classes.sliderRail,
                        track: classes.sliderTrack,
                        thumb: classes.sliderThumb,
                        valueLabel: classes.sliderValueLabel,
                    }}
                />
                <PlayerActionsContainer
                    classes={classes}
                    getMovieDownloadUrl={handleGetMovieDownloadUrl}
                    setShowVideoPlayerActions={setShowVideoPlayerActions}
                    showPlayer={showPlayer}
                    handlePlayerOpen={handleActivatePlayerDialog}
                    handleOpenDetails={handleOpenFrameDetailsInGrid}
                    playerSettings={playerSettings}
                    handleChangePlayerSettings={handleChangePlayerSettings}
                    paramName={paramName}
                />
            </div>
        </div>
    );
};

export default PhotoOverlay;
